export const GOOGLE_MAPS_KEY = 'AIzaSyBlFHeJDyaRY_UOHQw6q6XHFvIdCK1F8HU'

let googleMapsLoader

export const liveLocationIcon = (maps) => ({
  path: maps.SymbolPath.CIRCLE,
  scale: 9,
  fillColor: '#1a73e8',
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 3,
})

/** Move a blue dot with the device GPS. markerHolder is a ref-like { current }. */
export const watchLiveLocation = (map, maps, markerHolder) => {
  if (!map || !maps || !navigator.geolocation) return () => {}
  const holder = markerHolder || { current: null }
  const watchId = navigator.geolocation.watchPosition((position) => {
    const point = { lat: position.coords.latitude, lng: position.coords.longitude }
    if (!holder.current) {
      holder.current = new maps.Marker({
        map,
        position: point,
        zIndex: 2000,
        title: 'Live location',
        icon: liveLocationIcon(maps),
      })
      return
    }
    holder.current.setMap(map)
    holder.current.setPosition(point)
  }, () => {}, { enableHighAccuracy: true, maximumAge: 3000, timeout: 12000 })
  return () => navigator.geolocation.clearWatch(watchId)
}

export const loadGoogleMaps = () => {
  if (window.google?.maps?.places) return Promise.resolve(window.google.maps)
  if (!googleMapsLoader) {
    googleMapsLoader = new Promise((resolve, reject) => {
      const finish = () => {
        if (window.google?.maps?.places) resolve(window.google.maps)
        else reject(new Error('Google Maps loaded without Places.'))
      }
      const existing = document.querySelector('script[data-google-maps="visit-planner"]')
      if (existing) {
        if (window.google?.maps?.places) finish()
        else {
          existing.addEventListener('load', finish)
          existing.addEventListener('error', () => reject(new Error('Could not load Google Maps.')))
        }
        return
      }
      const script = document.createElement('script')
      script.dataset.googleMaps = 'visit-planner'
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_KEY)}&libraries=places&v=weekly`
      script.async = true
      script.onload = finish
      script.onerror = () => reject(new Error('Could not load Google Maps.'))
      document.head.appendChild(script)
    })
  }
  return googleMapsLoader
}

const KARNATAKA_BOUNDS = { south: 11.5, west: 74.05, north: 18.48, east: 78.59 }
const NEARBY_KM = 25

let nearbyOrigin = null

export const rememberNearbyOrigin = (lat, lng) => {
  const latitude = Number(lat)
  const longitude = Number(lng)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return
  nearbyOrigin = { lat: latitude, lng: longitude }
}

const watchNearbyOrigin = () => {
  if (nearbyOrigin || !navigator.geolocation) return
  navigator.geolocation.getCurrentPosition(
    (pos) => rememberNearbyOrigin(pos.coords.latitude, pos.coords.longitude),
    () => {},
    { enableHighAccuracy: true, maximumAge: 120000, timeout: 8000 },
  )
}

const nearbyBounds = (maps, origin, km = NEARBY_KM) => {
  const dLat = km / 111
  const dLng = km / (111 * Math.max(0.2, Math.cos((origin.lat * Math.PI) / 180)))
  return new maps.LatLngBounds(
    { lat: origin.lat - dLat, lng: origin.lng - dLng },
    { lat: origin.lat + dLat, lng: origin.lng + dLng },
  )
}

const formatDistance = (meters) => {
  if (meters == null || Number.isNaN(Number(meters))) return ''
  if (meters < 1000) return `${Math.max(1, Math.round(meters))} m`
  return `${(meters / 1000).toFixed(1)} km`
}

export const karnatakaBounds = (maps) => new maps.LatLngBounds(
  { lat: KARNATAKA_BOUNDS.south, lng: KARNATAKA_BOUNDS.west },
  { lat: KARNATAKA_BOUNDS.north, lng: KARNATAKA_BOUNDS.east },
)

export const karnatakaPlaceOptions = (maps) => ({
  fields: ['place_id', 'geometry', 'name', 'formatted_address', 'address_components'],
  componentRestrictions: { country: 'in' },
  bounds: karnatakaBounds(maps),
  strictBounds: true,
})

const OTHER_STATE = /\b(tamil\s*nadu|delhi|maharashtra|uttarakhand|kerala|andhra|telangana|goa|puducherry|pondicherry|punjab|rajasthan|gujarat|uttar\s*pradesh|madhya\s*pradesh|bihar|west\s*bengal|odisha|orissa|assam|haryana|himachal|jharkhand|chhattisgarh|jammu|srinagar|meghalaya|manipur|nagaland|tripura|mizoram|sikkim|arunachal|ladakh|chandigarh|pune|mumbai|chennai|hyderabad|kolkata|dehradun|noida|gurugram|gurgaon)\b/i

const predictionInKarnataka = (item) => {
  const text = `${item?.description || ''} ${item?.structured_formatting?.secondary_text || ''}`
  if (/karnataka/i.test(text)) return true
  return !OTHER_STATE.test(text)
}

const placeLatLng = (place) => {
  const location = place?.geometry?.location
  if (!location) return null
  const lat = typeof location.lat === 'function' ? location.lat() : Number(location.lat)
  const lng = typeof location.lng === 'function' ? location.lng() : Number(location.lng)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

const pointInKarnataka = (lat, lng) => (
  lat >= KARNATAKA_BOUNDS.south && lat <= KARNATAKA_BOUNDS.north
  && lng >= KARNATAKA_BOUNDS.west && lng <= KARNATAKA_BOUNDS.east
)

export const isInKarnataka = (place) => {
  const point = placeLatLng(place)
  if (point) return pointInKarnataka(point.lat, point.lng)
  const components = place?.address_components || []
  const state = components.find((item) => item.types?.includes('administrative_area_level_1'))
  if (state) return /karnataka/i.test(state.long_name || '') || state.short_name === 'KA'
  return /karnataka/i.test(place?.formatted_address || place?.name || '')
}

export const bindKarnatakaAutocomplete = (maps, input, onPlace, getOrigin) => {
  if (input._karnatakaCleanup) input._karnatakaCleanup()
  watchNearbyOrigin()
  const currentOrigin = () => {
    const picked = getOrigin?.()
    const lat = Number(picked?.lat)
    const lng = Number(picked?.lng)
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng }
    return nearbyOrigin
  }
  input.setAttribute('autocomplete', 'off')
  const service = new maps.places.AutocompleteService()
  const details = new maps.places.PlacesService(document.createElement('div'))
  const list = document.createElement('div')
  list.style.cssText = 'position:absolute;z-index:4000;background:#fff;border:1px solid #dadce0;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,.18);max-height:280px;overflow:auto;display:none;font-family:Roboto,Arial,sans-serif;'
  document.body.appendChild(list)

  const hide = () => { list.style.display = 'none' }
  const position = () => {
    const rect = input.getBoundingClientRect()
    list.style.left = `${rect.left + window.scrollX}px`
    list.style.top = `${rect.bottom + window.scrollY + 4}px`
    list.style.width = `${Math.max(rect.width, 260)}px`
  }
  const deliver = (place, fallbackLabel) => {
    const point = placeLatLng(place)
    if (!point || !isInKarnataka(place)) return
    const address = place.formatted_address || place.name || fallbackLabel || ''
    input.value = address
    onPlace({
      lat: point.lat,
      lng: point.lng,
      name: place.name || fallbackLabel || '',
      address,
    })
    hide()
  }
  const choose = (prediction) => {
    details.getDetails({
      placeId: prediction.place_id,
      fields: ['geometry', 'name', 'formatted_address', 'address_components'],
    }, (place, status) => {
      if (status === 'OK' && place) deliver(place, prediction.description)
    })
  }
  const showEmpty = () => {
    position()
    list.innerHTML = '<div style="padding:10px 12px;color:#5f6368;font-size:13px;">No private schools, colleges, or locations in Karnataka</div>'
    list.style.display = 'block'
  }
  const showPredictions = (predictions) => {
    const matches = predictions || []
    if (!matches.length) {
      showEmpty()
      return
    }
    position()
    list.innerHTML = ''
    let lastGroup = ''
    matches.forEach((item) => {
      const near = item.distance_meters != null && item.distance_meters <= NEARBY_KM * 1000
      const group = near ? 'Nearby' : 'Farther in Karnataka'
      if (item.distance_meters != null && group !== lastGroup) {
        lastGroup = group
        const head = document.createElement('div')
        head.textContent = group
        head.style.cssText = 'padding:6px 12px;font-size:11px;font-weight:700;color:#5f6368;background:#f8f9fa;'
        list.appendChild(head)
      }
      const row = document.createElement('button')
      row.type = 'button'
      row.style.cssText = 'display:block;width:100%;text-align:left;border:0;background:#fff;padding:10px 12px;cursor:pointer;font-size:14px;'
      const tag = document.createElement('span')
      tag.textContent = item.kind || 'Location'
      tag.style.cssText = 'display:inline-block;min-width:64px;margin-right:8px;color:#1a73e8;font-size:11px;font-weight:700;'
      row.appendChild(tag)
      const label = document.createElement('span')
      label.textContent = item.description || ''
      row.appendChild(label)
      const distance = formatDistance(item.distance_meters)
      if (distance) {
        const dist = document.createElement('span')
        dist.textContent = distance
        dist.style.cssText = 'float:right;color:#5f6368;font-size:12px;margin-left:8px;'
        row.appendChild(dist)
      }
      row.addEventListener('mouseenter', () => { row.style.background = '#f1f3f4' })
      row.addEventListener('mouseleave', () => { row.style.background = '#fff' })
      row.addEventListener('mousedown', (event) => event.preventDefault())
      row.addEventListener('click', () => choose(item))
      list.appendChild(row)
    })
    const credit = document.createElement('div')
    credit.textContent = 'powered by Google'
    credit.style.cssText = 'padding:6px 12px;text-align:right;color:#9aa0a6;font-size:11px;'
    list.appendChild(credit)
    list.style.display = 'block'
  }
  let timer
  const search = () => {
    const query = input.value.trim()
    if (query.length < 2) {
      hide()
      return
    }
    const here = currentOrigin()
    const origin = here ? new maps.LatLng(here.lat, here.lng) : null
    const localBounds = here ? nearbyBounds(maps, here) : karnatakaBounds(maps)
    const ask = (extra) => new Promise((resolve) => {
      service.getPlacePredictions({
        componentRestrictions: { country: 'in' },
        bounds: localBounds,
        strictBounds: true,
        ...(origin ? { origin } : {}),
        ...extra,
      }, (predictions, status) => {
        resolve(status === 'OK' ? predictions || [] : [])
      })
    })
    const askWider = (extra) => new Promise((resolve) => {
      service.getPlacePredictions({
        componentRestrictions: { country: 'in' },
        bounds: karnatakaBounds(maps),
        strictBounds: true,
        ...(origin ? { origin } : {}),
        ...extra,
      }, (predictions, status) => {
        resolve(status === 'OK' ? predictions || [] : [])
      })
    })
    const govt = /government|\bgovt\b|kendriya vidyalaya|jawahar navodaya|navodaya vidyalaya|\b(?:ghps|ghs|guhps|gmhps|gpuc|gjc|gghs|gbhs)\b/i
    const queries = [
      { input: `${query} school` },
      { input: `${query} college` },
      { input: query, types: ['geocode'] },
      { input: query, types: ['(regions)'] },
    ]
    Promise.all([
      ...queries.map((extra) => ask(extra)),
      ...queries.map((extra) => askWider(extra)),
    ]).then((groups) => {
      const [schoolRows, collegeRows, areaRows, regionRows, wideSchools, wideColleges, wideAreas, wideRegions] = groups
      if (input.value.trim() !== query) return
      const seen = new Set()
      const rows = []
      const push = (item, kind) => {
        if (!item?.place_id || seen.has(item.place_id) || !predictionInKarnataka(item)) return
        if (kind !== 'Location' && govt.test(item.description || '')) return
        seen.add(item.place_id)
        rows.push({ ...item, kind })
      }
      schoolRows.forEach((item) => {
        const text = item.description || ''
        if (/college|university/i.test(text)) push(item, 'College')
        else if (/school|vidyalaya|vidya|academy|convent/i.test(text)) push(item, 'School')
      })
      collegeRows.forEach((item) => {
        if (/college|university|institute|\bpuc\b/i.test(item.description || '')) push(item, 'College')
      })
      areaRows.forEach((item) => push(item, 'Location'))
      regionRows.forEach((item) => push(item, 'Location'))
      wideSchools.forEach((item) => {
        const text = item.description || ''
        if (/college|university/i.test(text)) push(item, 'College')
        else if (/school|vidyalaya|vidya|academy|convent/i.test(text)) push(item, 'School')
      })
      wideColleges.forEach((item) => {
        if (/college|university|institute|\bpuc\b/i.test(item.description || '')) push(item, 'College')
      })
      wideAreas.forEach((item) => push(item, 'Location'))
      wideRegions.forEach((item) => push(item, 'Location'))
      rows.sort((a, b) => {
        const aNear = a.distance_meters != null && a.distance_meters <= NEARBY_KM * 1000
        const bNear = b.distance_meters != null && b.distance_meters <= NEARBY_KM * 1000
        if (aNear !== bNear) return aNear ? -1 : 1
        if (a.distance_meters == null && b.distance_meters == null) return 0
        if (a.distance_meters == null) return 1
        if (b.distance_meters == null) return -1
        return a.distance_meters - b.distance_meters
      })
      showPredictions(rows)
    })
  }
  const onInput = () => {
    clearTimeout(timer)
    timer = setTimeout(search, 200)
  }
  const onKeyDown = (event) => {
    if (event.key === 'Escape') hide()
    if (event.key !== 'Enter') return
    event.preventDefault()
    const query = input.value.trim()
    if (!query) return
    geocodePlace(query).then((found) => {
      onPlace({ lat: found.lat, lng: found.lng, name: query, address: found.address || query })
      input.value = found.address || query
      hide()
    }).catch(() => showEmpty())
  }
  const onFocus = () => { if (input.value.trim().length >= 2) search() }
  const onOutside = (event) => {
    if (event.target === input || list.contains(event.target)) return
    hide()
  }
  input.addEventListener('input', onInput)
  input.addEventListener('keydown', onKeyDown)
  input.addEventListener('focus', onFocus)
  document.addEventListener('mousedown', onOutside)
  window.addEventListener('resize', hide)
  input._karnatakaCleanup = () => {
    clearTimeout(timer)
    input.removeEventListener('input', onInput)
    input.removeEventListener('keydown', onKeyDown)
    input.removeEventListener('focus', onFocus)
    document.removeEventListener('mousedown', onOutside)
    window.removeEventListener('resize', hide)
    list.remove()
    input._karnatakaCleanup = null
  }
  return null
}

export const attachGooglePlaceSearch = (input, onPlace) => {
  if (!input) return Promise.resolve(null)
  return loadGoogleMaps().then((maps) => bindKarnatakaAutocomplete(maps, input, onPlace))
}

export const reverseGeocode = (lat, lng) => loadGoogleMaps().then((maps) => new Promise((resolve) => {
  const geocoder = new maps.Geocoder()
  geocoder.geocode({ location: { lat: Number(lat), lng: Number(lng) } }, (results, status) => {
    if (status !== 'OK' || !results?.[0]) {
      resolve({ address: '', locality: '' })
      return
    }
    const result = results[0]
    const component = (type) => result.address_components?.find((item) => item.types.includes(type))?.long_name || ''
    resolve({
      address: result.formatted_address || '',
      locality: component('sublocality_level_1') || component('sublocality') || component('locality') || component('administrative_area_level_2') || '',
    })
  })
}))

export const geocodePlace = (query) => loadGoogleMaps().then((maps) => new Promise((resolve, reject) => {
  const geocoder = new maps.Geocoder()
  geocoder.geocode({
    address: `${query}, Karnataka, India`,
    componentRestrictions: { country: 'IN', administrativeArea: 'Karnataka' },
    bounds: karnatakaBounds(maps),
  }, (results, status) => {
    const match = (results || []).find((item) => isInKarnataka(item))
    const location = match?.geometry?.location
    if (status !== 'OK' || !location) {
      reject(new Error('Could not find that place in Karnataka.'))
      return
    }
    resolve({
      lat: location.lat(),
      lng: location.lng(),
      address: match.formatted_address || '',
    })
  })
}))
